// import libs
import debounce from 'lodash/debounce';
import { median } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { sendMessage, DOMReady } from './utils';
import 'whatwg-fetch';

// import chart frame
import Chart from './chart';

// import base styles'
import './app.scss';

import footnotes from './footnotes.json';

// enable hot module replacement
if ( module.hot ) {
	module.hot.accept();
}

const DAY_IN_MS = 60 * 60 * 24 * 1000;

// Count backward to the first week whose sample count is >50% of the ealier median
function calculatePreliminaryWeeks( data ) {
	const recentWeeks = data.slice( data.length - 10, data.length - 5 );
	const medianN = median( recentWeeks.map( ( d ) => d.n_samples ) );

	const preliminaryWeekCount = [...data]
		.reverse()
		.findIndex( ( d ) => d.n_samples > medianN * 0.4 );

	return Math.max( preliminaryWeekCount, 1 );
}

// config initials

const params = {
	project: 'covid-variants',
	file: 'covid-19-variants-canada.json',
};

const dataUrl = `https://globalnews.ca/gnca-ajax-redesign/interactive-data/${encodeURI( JSON.stringify( params ) )}`;

DOMReady( async () => {
	const res = await fetch( dataUrl );
	const { data, last_updated } = await res.json();

	const variantData = data.map( ( d ) => {
		const item = {};

		Object.entries( d ).forEach( ([key, value]) => {
			if ( 'week' === key ) {
				item.date = new Date( `${value}T20:00:00-04:00` );
			} else {
				item[key] = + value;
			}
		});

		return item;
	}).filter( ( d ) => d.date >= new Date( '2020-10-31' ) );

	const minDate = variantData[0].date;
	const maxDate =  new Date( variantData[variantData.length - 1].date.getTime() + DAY_IN_MS * 7 );

	const preliminaryWeeks = calculatePreliminaryWeeks( variantData );

	const variantList = Object.keys( variantData[0]);

	const variants = [
		{
			key: 'voi',
			displayName: 'Variants of interest',
			color: '#FAA540',
		},
		{
			key: 'alpha',
			displayName: 'Alpha',
			color: '#C5BB2E',
		},
		{
			key: 'beta',
			displayName: 'Beta',
			color: '#8BC594',
		},
		{
			key: 'gamma',
			displayName: 'Gamma',
			color: '#93BBE1',
		},
		{
			key: 'delta',
			displayName: 'Delta',
			color: '#CBACDA',
		},
		{
			key: variantList.includes( 'omicron_ba1' ) ? 'omicron_ba1' : 'omicron',
			displayName: 'Omicron BA.1',
			color: '#C50077',
		},
		{
			key: 'omicron_ba2',
			displayName: 'Omicron BA.2',
			color: '#72124B',
		},
		{
			key: 'other_omicron',
			displayName: 'Other Omicron',
			color: '#4F1737',
		},
	];

	// Only display variants that are present in the data
	const activeVariants = variants.filter( ( d ) => variantList.includes( d.key ) );

	// Initialize chart frame
	Chart.init({
		data: variantData,
		bounds: {
			minY: 0,
			maxY: 1,
			minX: minDate,
			maxX: maxDate,
		},
		label: 'Variants',
		keys: activeVariants.map( ( d ) => d.key ),
		chartOrder: [
			'other_omicron',
			'omicron_ba2',
			variantList.includes( 'omicron_ba1' ) ? 'omicron_ba1' : 'omicron',
			'delta',
			'gamma',
			'beta',
			'alpha',
			'voi',
		],
		colors: activeVariants.map( ( d ) => d.color ),
		labels: new Map( activeVariants.map( ( d ) => [d.key, d.displayName]) ),
		preliminaryWeeks,
	});

	if ( last_updated ) {
		const formatter = timeFormat( '%B %d, %Y at %I:%M %p EST' );
		const $timeStamp = document.querySelector( '.l-interactive__updated time' );
		$timeStamp.innerText = formatter( new Date( last_updated ) );
	}

	const $legend = document.querySelector( '.l-interactive__legend' );

	activeVariants.forEach( ({ displayName, color }) => {
		$legend.insertAdjacentHTML( 'beforeend', `<li style="--seriesColor: ${color}">${displayName}</li>` );
	});

	const $footnote = document.querySelector( '.l-interactive__footnote' );
	$footnote.innerHTML = `<p>${footnotes.join( '</p><p>' )}</p>`;

	// Resize after content loaded
	sendMessage({
		height: document.documentElement.offsetHeight,
		action: 'setHeight',
	});

	// bind event to capture resize and orientation change
	const resizeHandler = debounce( () => {
		Chart.render();
		sendMessage({
			height: document.documentElement.offsetHeight,
			action: 'setHeight',
		});
	}, 200 );
	window.addEventListener( 'orientationchange', resizeHandler );
	window.addEventListener( 'resize', resizeHandler );
});
